// Conversation state: loading history, optimistic send, the live events channel (OPEN-23), stop,
// and retry through regenerate. The POST answers JSON at once; every viewer (the poster included)
// sees the assistant reply arrive through GET /api/conversations/:id/events.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ConversationEvent,
  type ContextUsage,
  type Conversation,
  type ConversationPresenceEvent,
  type DocumentKind,
} from '@jabberr/core';
import { api, ApiError, errorMessage, notifyUnauthorized } from '../lib/api.js';
import { parseJsonData, readSseStream } from '../lib/sse.js';
import {
  CONTEXT_TOO_LONG,
  fromServerTurns,
  mergeTurns,
  optimisticUserTurn,
  statusFromOutcome,
  type ViewTool,
  type ViewTurn,
} from '../lib/turns.js';

export interface ChatState {
  conversation: Conversation | null;
  turns: ViewTurn[];
  loading: boolean;
  loadError: string | null;
  streaming: boolean;
  /** How full the next prompt is (from GET and from every usage event). */
  contextUsage: ContextUsage | null;
  /** People watching this conversation right now (the channel's presence snapshots). */
  presence: ConversationPresenceEvent['members'];
  send: (content: string, clientTurnId?: string, purpose?: DocumentKind) => Promise<void>;
  stop: () => void;
  /** A failed user turn: regenerate the reply for the turn the server kept, or resend one it never saw. */
  retry: (turn: ViewTurn) => Promise<void>;
  reload: () => Promise<void>;
  setConversation: (c: Conversation) => void;
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException ? err.name === 'AbortError' : err instanceof Error && err.name === 'AbortError';
}

/**
 * Next reconnect wait in ms: double the previous wait, capped at 30 s, with up to
 * 20 percent upward jitter. Pure so tests can pin it with a fixed random.
 */
export function nextReconnectDelay(previousMs: number, random: () => number): number {
  const base = Math.min(previousMs * 2, 30_000);
  return Math.round(base * (1 + random() * 0.2));
}

export function useChat(conversationId: string, currentUserId?: string | null): ChatState {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [turns, setTurns] = useState<ViewTurn[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [contextUsage, setContextUsage] = useState<ContextUsage | null>(null);
  /** People watching this conversation right now, from the channel's presence snapshots. */
  const [presence, setPresence] = useState<ConversationPresenceEvent['members']>([]);
  /** Server turn ids of optimistic assistant turns (turn event not seen yet). */
  const pendingAssistant = useRef<Set<string>>(new Set());
  const mounted = useRef(true);
  /** Latest turns, so event handlers can read them without side effects inside updaters. */
  const turnsRef = useRef<ViewTurn[]>([]);
  turnsRef.current = turns;

  const patch = useCallback((id: string, fn: (t: ViewTurn) => ViewTurn) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? fn(t) : t)));
  }, []);

  const reload = useCallback(async () => {
    try {
      const { conversation: c, turns: serverTurns, context } = await api.conversations.get(conversationId);
      if (!mounted.current) return;
      setConversation(c);
      setContextUsage(context);
      setTurns((prev) => mergeTurns(prev, fromServerTurns(serverTurns)));
      setLoadError(null);
    } catch (err) {
      if (mounted.current) setLoadError(errorMessage(err));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [conversationId]);

  /** One event from the conversation's channel, applied to the local view. */
  const applyEvent = useCallback(
    (msg: { event: string; data: unknown }) => {
      const parsed = ConversationEvent.safeParse({ event: msg.event, data: msg.data });
      if (!parsed.success) return;
      const ev = parsed.data;
      switch (ev.event) {
        case 'role_changed':
          void reload();
          break;
        case 'turn': {
          const t = ev.data;
          if (t.kind === 'user') {
            // Our own POST landed: fill in what the server assigned. The match runs INSIDE the updater, against
            // the state React will really apply it to, so an optimistic turn queued a moment ago is still
            // found; it is a pure map and a no-op when nothing matches.
            if (t.clientTurnId !== null) {
              setTurns((p) =>
                p.map((x) => {
                  if (x.kind !== 'user' || x.clientTurnId !== t.clientTurnId) return x;
                  const { error: _e, ...rest } = x;
                  return { ...rest, key: t.turnId, id: t.turnId, seq: t.seq, status: 'completed', persisted: true, mentions: t.mentions, interjectP: t.interjectP ?? null };
                }),
              );
            }
            // The side effect stays outside any updater (React may call an updater twice). The ref can lag a
            // just-queued update by one render; the worst case is one reload that was not needed.
            const known = turnsRef.current;
            const ours = t.clientTurnId !== null && known.some((x) => x.kind === 'user' && x.clientTurnId === t.clientTurnId);
            if (!ours && !known.some((x) => x.id === t.turnId)) {
              // Another member posted (or we missed the POST): fetch the content.
              void reload();
            }
            // A turn that @mentions the current user: wake the mentions inbox (badge, banner,
            // tab title) and notify when the page is hidden. Side effect, so outside updaters.
            if (currentUserId && t.mentions.includes(currentUserId)) {
              window.dispatchEvent(new CustomEvent('jabberr:mention', { detail: { conversationId } }));
            }
          } else {
            setTurns((prev) => {
              if (prev.some((x) => x.id === t.turnId)) return prev;
              const placeholder: ViewTurn = {
                key: t.turnId,
                id: t.turnId,
                clientTurnId: null,
                kind: 'assistant',
                status: 'streaming',
                text: '',
                reasoning: '',
                tools: [],
                purpose: null,
                rating: null,
                mentions: [],
                interjectP: null,
                seq: t.seq,
                persisted: true,
                createdAt: new Date().toISOString(),
              };
              return [...prev, placeholder];
            });
            setStreaming(true);
          }
          break;
        }
        case 'interject': {
          const { turnId, p } = ev.data;
          setTurns((prev) => prev.map((x) => (x.id === turnId && x.kind === 'user' ? { ...x, interjectP: p } : x)));
          break;
        }
        case 'keen_outcome': {
          const { turnId, outcome, p } = ev.data;
          setTurns((prev) => prev.map((x) => (x.id === turnId && x.kind === 'user' ? { ...x, keenOutcome: outcome, interjectP: p } : x)));
          break;
        }
        case 'reasoning': {
          if (ev.data.text) patch(ev.data.assistantTurnId, (t) => ({ ...t, reasoning: t.reasoning + ev.data.text }));
          break;
        }
        case 'content': {
          if (ev.data.text) patch(ev.data.assistantTurnId, (t) => ({ ...t, text: t.text + ev.data.text }));
          break;
        }
        case 'tool': {
          const tool: ViewTool = {
            callId: ev.data.callId,
            name: ev.data.name,
            label: ev.data.label,
            status: ev.data.status,
            ...(ev.data.outcome ? { outcome: ev.data.outcome } : {}),
          };
          patch(ev.data.assistantTurnId, (t) => {
            const rest = t.tools.filter((x) => x.callId !== tool.callId);
            return { ...t, tools: [...rest, tool] };
          });
          break;
        }
        case 'usage': {
          const { usedFraction, window } = ev.data;
          if (usedFraction !== undefined && window !== undefined) {
            setContextUsage((prev) => ({
              usedFraction,
              window,
              budget: prev?.budget ?? window,
              promptTokens: ev.data.promptTokens ?? prev?.promptTokens ?? 0,
              source: prev?.source ?? 'estimate',
            }));
          }
          break;
        }
        case 'presence': {
          setPresence(ev.data.members);
          break;
        }
        case 'done': {
          const { assistantTurnId, status, error, finishReason } = ev.data;
          pendingAssistant.current.delete(assistantTurnId);
          setTurns((prev) => {
            const a = prev.find((t) => t.id === assistantTurnId);
            if (!a) return prev;
            if (status === 'context_too_long') {
              return prev.map((t) => (t.id === assistantTurnId ? { ...t, status: 'failed', cancelReason: CONTEXT_TOO_LONG, error: error ?? status } : t));
            }
            const empty = a.text.length === 0 && a.reasoning.length === 0;
            if ((status === 'unavailable' || status === 'error') && empty) {
              // Keep the user turn with a retry affordance; drop the empty placeholder.
              const idx = prev.findIndex((t) => t.id === assistantTurnId);
              return prev
                .filter((t, i) => !(i === idx && t.id === assistantTurnId))
                .map((t) => {
                  if (idx > 0 && prev[idx - 1] && prev[idx - 1]!.id === t.id) {
                    return { ...t, status: statusFromOutcome(status), error: error ?? status, persisted: true };
                  }
                  return t;
                });
            }
            return prev.map((t) =>
              t.id === assistantTurnId ? { ...t, status: statusFromOutcome(status), ...(error ? { error } : {}), ...(finishReason === 'length' ? { truncated: true } : {}) } : t,
            );
          });
          setStreaming(false);
          // Refresh the conversation row (lastTurnSeq, title) and the usage without touching local turns.
          api.conversations
            .get(conversationId)
            .then(({ conversation: c, context }) => {
              if (!mounted.current) return;
              setConversation(c);
              setContextUsage(context);
            })
            .catch(() => undefined);
          break;
        }
      }
    },
    [conversationId, currentUserId, patch, reload],
  );

  // The live events channel: subscribe for the life of the view, reconnect with exponential
  // backoff (1 s doubling to a 30 s ceiling, up to 20 percent jitter), paused while the page
  // is hidden. A connection that delivered an event, or stayed open for 10 s, resets the delay.
  useEffect(() => {
    mounted.current = true;
    let stopped = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let delay = 1000;
    let connectedAt = 0;
    let delivered = false;
    let awaitingReconnect = false;
    const controller = new AbortController();

    const scheduleReconnect = (): void => {
      if (stopped || awaitingReconnect) return;
      awaitingReconnect = true;
      // While the page is hidden, hold off entirely; visibilitychange resumes at once.
      if (document.visibilityState === 'hidden') return;
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        awaitingReconnect = false;
        if (stopped) return;
        // Missed frames while disconnected: reload once, then reconnect.
        void reload();
        void attempt();
      }, delay);
      delay = nextReconnectDelay(delay, Math.random);
    };

    const attempt = async (): Promise<void> => {
      // Reset per attempt: a connection that never opened has no age, and must not borrow the last one's.
      connectedAt = 0;
      delivered = false;
      try {
        const res = await fetch(`/api/conversations/${conversationId}/events`, {
          headers: { accept: 'text/event-stream' },
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (res.status === 401) {
          notifyUnauthorized(); // bounce to login through the shared listener
          return;
        }
        if (!res.ok || !res.body) throw new Error(`events: HTTP ${res.status}`);
        connectedAt = Date.now();
        delivered = false;
        for await (const msg of readSseStream(res.body, controller.signal)) {
          if (stopped) return;
          delivered = true;
          applyEvent({ event: msg.event, data: parseJsonData(msg.data) });
        }
      } catch (err) {
        if (isAbort(err) || stopped) return;
      }
      if (stopped) return;
      // A connection that delivered something, or simply stayed open, earns a fresh delay.
      if (delivered || (connectedAt > 0 && Date.now() - connectedAt >= 10_000)) delay = 1000;
      scheduleReconnect();
    };

    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (stopped || !awaitingReconnect || retryTimer) return;
      awaitingReconnect = false;
      void reload();
      void attempt();
    };

    document.addEventListener('visibilitychange', onVisibility);
    void attempt();

    return () => {
      stopped = true;
      mounted.current = false;
      if (retryTimer) clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      controller.abort();
    };
  }, [conversationId, applyEvent, reload]);

  // Initial load.
  useEffect(() => {
    setTurns([]);
    setConversation(null);
    setContextUsage(null);
    setPresence([]);
    setLoading(true);
    setLoadError(null);
    void reload();
  }, [reload]);

  const send = useCallback(
    async (content: string, clientTurnId: string = crypto.randomUUID(), purpose?: DocumentKind) => {
      if (streaming) return;
      const user = { ...optimisticUserTurn(clientTurnId, content), purpose: purpose ?? null };
      setTurns((prev) => [...prev, user]);
      try {
        const body = await api.conversations.send(conversationId, { clientTurnId, content, ...(purpose ? { purpose } : {}) });
        if (!mounted.current) return;
        // The turn event (ours or the channel's) fills in ids; mark persisted at once so a slow
        // channel never makes the composer look lost.
        setTurns((prev) =>
          prev.map((t) => {
            if (t !== user) return t;
            const { error: _e, ...rest } = t;
            return { ...rest, id: body.userTurnId, seq: body.seq, status: 'completed', persisted: true };
          }),
        );
        if (body.assistantTurnId) {
          setStreaming(true);
          // The assistant placeholder normally arrives as a turn event; create it only if we won the race.
          setTurns((prev) => {
            if (prev.some((t) => t.id === body.assistantTurnId)) return prev;
            return [
              ...prev,
              {
                key: body.assistantTurnId!,
                id: body.assistantTurnId!,
                clientTurnId: null,
                kind: 'assistant',
                status: 'streaming',
                text: '',
                reasoning: '',
                tools: [],
                purpose: purpose ?? null,
                rating: null,
                mentions: [],
                interjectP: null,
                seq: body.seq + 1,
                persisted: true,
                createdAt: new Date().toISOString(),
              },
            ];
          });
          pendingAssistant.current.add(body.assistantTurnId);
        }
      } catch (err) {
        if (!mounted.current) return;
        if (err instanceof ApiError && err.code === 'duplicate_client_turn_id') {
          setTurns((prev) => prev.filter((t) => t !== user));
          await reload();
          return;
        }
        setTurns((prev) =>
          prev.map((t) =>
            t === user
              ? { ...t, status: 'failed', persisted: false, error: err instanceof ApiError && err.code === 'generation_in_flight' ? 'Jabberr is already replying; try again in a moment.' : errorMessage(err) }
              : t,
          ),
        );
      }
    },
    [conversationId, reload, streaming],
  );

  const stop = useCallback(() => {
    // One generation at a time: whichever assistant turn is streaming is the one to cancel.
    // Read the latest turns from the ref; the network call must sit outside any updater
    // because React may invoke an updater more than once.
    const active = turnsRef.current.find((t) => t.kind === 'assistant' && t.status === 'streaming' && t.id);
    if (active?.id) api.conversations.cancel(conversationId, active.id).catch(() => undefined);
  }, [conversationId]);

  const retry = useCallback(
    async (turn: ViewTurn) => {
      if (turn.kind !== 'user' || streaming) return;
      if (turn.persisted && turn.id) {
        // The server kept the turn: re-run generation for it. Never append a second copy.
        setTurns((prev) =>
          prev.map((t) => {
            if (t.key !== turn.key) return t;
            const { error: _e, ...rest } = t;
            return { ...rest, status: 'completed' as const };
          }),
        );
        try {
          await api.conversations.regenerate(conversationId, turn.id);
          setStreaming(true);
        } catch (err) {
          if (err instanceof ApiError && err.code === 'generation_in_flight') {
            setTurns((prev) => prev.map((t) => (t.key === turn.key ? { ...t, status: 'failed', error: 'Jabberr is already replying; try again in a moment.' } : t)));
          } else {
            await reload();
          }
        }
      } else {
        setTurns((prev) => prev.filter((t) => t.key !== turn.key));
        await send(turn.text, turn.clientTurnId ?? undefined);
      }
    },
    [conversationId, reload, send, streaming],
  );

  return { conversation, turns, loading, loadError, streaming, contextUsage, presence, send, stop, retry, reload, setConversation };
}
